import { DEFAULT_LOCALE, Locale } from "../../locales"
import { COMPANY_STATISTICS_TITLES } from "./company-statistics-titles"
import {
  CompanyStatisticQueryResult,
  FormattedValueMap,
  ValueFormat,
} from "./types"

export const thousand = 1000
export const million = thousand * thousand
export const billion = million * thousand

const LOCALIZED_FORMAT_VALUES = [
  {
    fullWord: {
      en: "billion",
      de: "Milliarde",
    },
    singleLetter: {
      en: "B",
      de: " Mrd.",
    },
    value: billion,
  },
  {
    fullWord: {
      en: "million",
      de: "Million",
    },
    singleLetter: {
      en: "M",
      de: " Mio.",
    },
    value: million,
  },
  {
    fullWord: {
      en: "thousand",
      de: "Tausend",
    },
    singleLetter: {
      en: "K",
      de: ".000",
    },
    value: thousand,
  },
] as const satisfies Array<FormattedValueMap>

export const formatValue = (
  value: number,
  format: ValueFormat = "numerical",
  locale: Locale = DEFAULT_LOCALE,
): string => {
  if (format === "numerical") {
    return value.toLocaleString(locale)
  }
  const formatData = LOCALIZED_FORMAT_VALUES.find(f => value >= f.value)
  if (!formatData) {
    throw new Error("No format data found for value")
  }
  const baseValue = (value / (formatData?.value ?? 1)).toFixed(0)
  const unitText = formatData[format][locale]

  const spaceBetweenNumberAndFormat = format === "fullWord" ? " " : ""

  return `${baseValue}${spaceBetweenNumberAndFormat}${unitText}`
}

/**
 * Verifies that all expected company statistics are present in the query results. Throws an error if it's not, so that the build fails.
 * The build will only fail when using the `gatsby build` command, not when running the development server.
 * @param queryResults the result of the GraphQL query for company statistics
 */
export function validateCompanyStatistics(
  queryResults: CompanyStatisticQueryResult,
) {
  const queriedStats = queryResults.datoCms.allCompanyStatisticValues
  const queriedTitles = queriedStats.map(n => n.title)
  const missingTitles = COMPANY_STATISTICS_TITLES.filter(
    title => !queriedTitles.includes(title),
  )
  if (missingTitles.length) {
    throw new Error(
      `The following company statistics are expected on the code, but missing on Dato: ${missingTitles.join(", ")}. To fix this, either add the missing values on https://tremendous-marketing-website.admin.datocms.com/editor/item_types/Mzf1MFzcT-SJ-zdmROc4SQ/items, or remove them from "company-statistics-titles.ts".`,
    )
  }
}
